import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Table,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  getInvoiceDeletionPreview,
  deleteInvoice,
} from "../../afinanceapi";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import "./acc-list.css";

/**
 * InvoiceDeletionModal — single entry point for the invoice-deletion flow.
 *
 * Loads a deletion-preview from the backend on open to surface block
 * reasons (active receipts / Finalised-paid claim). If blocked by an
 * active receipt the user gets a "Reverse receipt" CTA that hands off to
 * the receipt-reversal flow via the parent's `onOpenReceiptReversal`
 * callback. Otherwise the user types a reason and submits — SuperAdmin
 * triggers the cascade directly (claim, credit-note lines, refunds);
 * Finance creates an APPROVAL task per SuperAdmin in the practice and
 * waits.
 *
 * Visual conventions: see /CLAUDE.md frontend design system §1.8 (modals)
 * and §1.8.1 (destructive confirmations). Mirrors ReceiptReversalModal
 * so the two flows feel like halves of the same gesture.
 */
const ZAR = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
});

function formatDate(d) {
  if (!d) return "—";
  try {
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return String(d);
    return parsed.toLocaleDateString("en-GB");
  } catch {
    return String(d);
  }
}

export default function InvoiceDeletionModal({
  show,
  onHide,
  invoice,
  onDeleted,
  onOpenReceiptReversal,
}) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [closeAlert, setCloseAlert] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  // Role derived from the JWT — same union pattern used everywhere
  // (role / roles / permissions). Drives the SA-direct vs Finance-request
  // button label + variant.
  const role = useMemo(() => {
    const t =
      sessionStorage.getItem("userToken") ||
      sessionStorage.getItem("user");
    if (!t) return null;
    try {
      const claims = jwtDecode(t);
      const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
      const roles = new Set([
        ...asArray(claims.role),
        ...asArray(claims.roles),
        ...asArray(claims.permissions),
      ]);
      if (roles.has("SuperAdmin")) return "SuperAdmin";
      if (roles.has("Finance")) return "Finance";
      return "Other";
    } catch {
      return null;
    }
  }, []);
  const isSuperAdmin = role === "SuperAdmin";

  useEffect(() => {
    if (!show) return;
    setReason("");
    setSubmitError(null);
    setCloseAlert(false);
    setHiddenForSuccess(false);
    setPreview(null);
    if (!invoice?._id) return;
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      setPreviewError(null);
      try {
        const data = await getInvoiceDeletionPreview(invoice._id);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(
            err?.response?.data?.error ||
              err?.message ||
              "Failed to load deletion preview."
          );
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, invoice?._id]);

  function handleClose() {
    if (reason.trim()) {
      setCloseAlert(true);
    } else {
      onHide();
    }
  }

  async function handleConfirm() {
    if (submitting) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setSubmitError("Reason is required.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await deleteInvoice(invoice._id, { reason: trimmed });
      if (result.status === "done") {
        showToast(
          "success",
          `Invoice ${invoice.invoiceNumber || ""} deleted.`
        );
        setHiddenForSuccess(true);
        setTimeout(() => {
          onHide();
          onDeleted?.({ status: "done", summary: result.summary });
        }, 1200);
      } else if (result.status === "pending_approval") {
        const n = result.taskIds?.length || 0;
        showToast(
          "success",
          `Approval requested — sent to ${n} SuperAdmin${n === 1 ? "" : "s"}.`
        );
        setHiddenForSuccess(true);
        setTimeout(() => {
          onHide();
          onDeleted?.({
            status: "pending_approval",
            approvalGroupId: result.approvalGroupId,
            taskIds: result.taskIds,
          });
        }, 1500);
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      const details = err?.response?.data?.details || {};
      if (code === "has_active_receipt") {
        const list = (details.receiptNumbers || []).join(", ");
        setSubmitError(
          `This invoice has an active receipt allocated to it${
            list ? ` (${list})` : ""
          }. Reverse the receipt first.`
        );
      } else if (code === "claim_blocks_deletion") {
        setSubmitError(
          `You can't delete — the linked claim is in status "${details.claimStatus}". Reverse the receipt first to clear the scheme payment.`
        );
      } else if (code === "already_deleted") {
        setSubmitError("This invoice has already been deleted.");
      } else if (code === "no_super_admins_in_practice") {
        setSubmitError(
          "This practice has no SuperAdmins configured. Contact your administrator."
        );
      } else {
        setSubmitError(code || err?.message || "Deletion failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const invoiceNumber = invoice?.invoiceNumber || "";
  const buttonLabel = isSuperAdmin ? "Confirm deletion" : "Confirm request";

  return (
    <>
      <Modal
        show={show && !hiddenForSuccess}
        onHide={handleClose}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Invoice Deletion{invoiceNumber ? ` — ${invoiceNumber}` : ""}
          </Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0 small"
          >
            <span>
              Are you sure you want to close? Changes will not be saved.
            </span>
            <div className="ms-auto d-flex gap-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Discard and close</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setCloseAlert(false);
                    onHide();
                  }}
                  aria-label="Discard and close"
                >
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Keep editing</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCloseAlert(false)}
                  aria-label="Keep editing"
                >
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}

        <Modal.Body>
          {loadingPreview && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 mb-0 text-muted">
                Loading deletion preview…
              </p>
            </div>
          )}

          {previewError && (
            <Alert variant="danger" className="mb-0">
              {previewError}
            </Alert>
          )}

          {!loadingPreview && preview && (
            <>
              {/* Blocked state — receipt(s) attached */}
              {preview.blockReason === "has_active_receipt" && (
                <>
                  <Alert variant="warning" className="mb-3">
                    You can't delete invoice{" "}
                    <strong>{preview.invoiceNumber}</strong> for{" "}
                    <strong>{ZAR.format(preview.totalAmount || 0)}</strong>{" "}
                    while an <strong>active receipt</strong> is allocated to
                    it. Reverse the receipt first, then return here.
                  </Alert>
                  <h6 className="text-primary fw-bold mb-2">
                    Blocking receipt
                    {(preview.blockingReceipts || []).length === 1 ? "" : "s"}
                  </h6>
                  <Table bordered hover responsive size="sm" className="mb-0">
                    <thead className="text-center">
                      <tr>
                        <th className="text-primary fw-semibold">Number</th>
                        <th className="text-primary fw-semibold">Date</th>
                        <th className="text-primary fw-semibold acc-list-num">
                          Receipt total
                        </th>
                        <th className="text-primary fw-semibold acc-list-num">
                          Allocated to this invoice
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.blockingReceipts || []).map((r) => (
                        <tr key={r._id}>
                          <td>{r.receiptNumber}</td>
                          <td>{formatDate(r.receiptDate)}</td>
                          <td className="acc-list-num">
                            {ZAR.format(r.receiptTotalAmount || 0)}
                          </td>
                          <td className="acc-list-num">
                            {ZAR.format(r.allocationAmount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Blocked state — claim is scheme-paid */}
              {preview.blockReason === "claim_blocks_deletion" && (
                <Alert variant="warning" className="mb-0">
                  You can't delete invoice{" "}
                  <strong>{preview.invoiceNumber}</strong> — the linked claim
                  is in status{" "}
                  <strong>{preview.claimStatus}</strong>. The scheme has
                  recorded a payment against this claim. Reverse the
                  associated receipt first to clear the scheme settlement,
                  then return here.
                </Alert>
              )}

              {/* Already-deleted edge case (should never render in practice) */}
              {preview.blockReason === "already_deleted" && (
                <Alert variant="info" className="mb-0">
                  This invoice has already been deleted.
                </Alert>
              )}

              {/* Allowed state */}
              {preview.canDelete && (
                <>
                  <Alert variant="warning" className="mb-3">
                    You are about to <strong>delete</strong> invoice{" "}
                    <strong>{preview.invoiceNumber}</strong> for{" "}
                    <strong>{ZAR.format(preview.totalAmount || 0)}</strong>.
                    This deletion is<strong> permanent</strong> — but the audit trail
                    is preserved.
                    {preview.claimStatus && (
                      <>
                        {" "}
                        The linked claim (status{" "}
                        <strong>{preview.claimStatus}</strong>) will be
                        deleted at the same time.
                      </>
                    )}
                  </Alert>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Reason <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is this invoice being deleted?"
                    />
                  </Form.Group>
                  {submitError && (
                    <Alert variant="danger" className="py-2 mb-0 small">
                      {submitError}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {/* Allowed-state footer: destructive primary + cancel */}
          {preview?.canDelete && (
            <>
              <Button
                variant="danger"
                onClick={handleConfirm}
                disabled={submitting || !reason.trim()}
                style={{ minWidth: 140 }}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  <>
                    <i className="fa-solid fa-trash me-2" />
                    {buttonLabel}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={submitting}
                style={{ minWidth: 140 }}
              >
                Cancel
              </Button>
            </>
          )}

          {/* Blocked-state footer: receipt handoff + close */}
          {preview && !preview.canDelete && (
            <>
              {preview.blockReason === "has_active_receipt" &&
                typeof onOpenReceiptReversal === "function" && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onHide();
                      onOpenReceiptReversal();
                    }}
                    style={{ minWidth: 140 }}
                  >
                    <i className="fa-solid fa-rotate-left me-2" />
                    Reverse receipt
                  </Button>
                )}
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                style={{ minWidth: 140 }}
              >
                Close
              </Button>
            </>
          )}

          {/* Initial loading / error footer */}
          {(loadingPreview || previewError) && !preview && (
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              style={{ minWidth: 140 }}
            >
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
